import React, {useState, useEffect} from "react";
import Forms from "./form/Forms";
import {Config} from "../config/Config";
import Button from "./forms/Button";
import useTranslation from "../hooks/useTranslation";
import {axiosInstance} from "../axios";
import {useUserData} from "../context/UserContext";
import ErrorMessage from "./ErrorMessage";
import {onChange, Tools} from "../config/Tools";
import Icon from "@mdi/react";
import {mdiChevronDown, mdiChevronUp} from "@mdi/js";

import "./SignUpModal.scss";

const SignUpModal = ({closeModal}) => {
    const {signUp} = useUserData();
    const {t} = useTranslation();
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [agreeWithTerms, setAgreeWithTerms] = useState(false);
    const [agreeWithRules, setAgreeWithRules] = useState(false);
    const [isTermsOpen, setIsTermsOpen] = useState(false);
    const [nationalityList, setNationalityList] = useState([]);
    const [statusList, setStatusList] = useState([]);
    const [formData, setFormData] = useState({});

    useEffect(() => {
        const fetchData = async () => {
            try {
                const nationalityList = await axiosInstance.get('/api/nationality');
                const statusList = await axiosInstance.get("/api/status");
                setNationalityList(nationalityList.data);
                setStatusList(statusList.data);
            } catch (e) {
                console.log(e);
                setError(e);
            } finally {
                setIsLoading(false);
            }
        };
        fetchData();
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!agreeWithRules) {
            setError("You must agree with the rules to continue");
            return;
        }
        try {
            setIsLoading(true);
            await signUp(formData);
            closeModal();
        } catch (e) {
            setError(e);
        } finally {
            setIsLoading(false);
        }
    }

    if (isLoading) {
        return Tools.getLoading();
    }

    const handleStatusValidation = (status) => {
        return statusList.find(s => s.type === status).acceptable ? null : "registrationOnlyForSomeVisaType";
    }

    const handleNationalityValidation = (nationality) => {
        return nationalityList.find(n => n.nationality === nationality).type.indexOf(6) !== -1 ? "registrationOnlyForNonEU" : null;
    }

    const sexOptions = [{label: t('male'), value: 'male'}, {label: t('female'), value: 'female'}];
    const nationalityOptions = nationalityList.map(({nationality}) => ({label: nationality, value: nationality}));
    const statusOptions = statusList.map((status) => ({label: t(status.type), value: status.type}));
    const iconPath = isTermsOpen ? mdiChevronUp : mdiChevronDown;
    const termsHtml = (
        <div>
            {t("termAndCondition")}
            <a target="_blank" rel="noopener noreferrer" href="https://icpraha.com/ochrana-osobnich-udaju/">
                <label>&nbsp;ICPraha</label>
            </a>
        </div>
    );

    return (
        <form onSubmit={handleSubmit} className="SignUpModal">
            <div className='modal-body modalBody'>
                <div>
                    <Forms.Text name="name" regex={Config.validationMap.textRegex} validationError='onlyLatinCharacter'
                                description={t("exactNameSurname")} value={formData.name}
                                onChange={onChange(setFormData)} required/>

                    <Forms.Text name="surname" description={t("exactNameSurname")} value={formData.surname}
                                regex={Config.validationMap.textRegex} validationError='onlyLatinCharacter'
                                onChange={onChange(setFormData)} required/>

                    <Forms.Select name="sex" options={sexOptions} value={formData.sex} required
                                  onChange={onChange(setFormData)} label={t('sex')}/>

                    <Forms.Select name="nationality" options={nationalityOptions} value={formData.nationality}
                                  onChange={onChange(setFormData)} required onValidate={handleNationalityValidation}/>

                    <Forms.Select name="residenceStatus" options={statusOptions} value={formData.residenceStatus}
                                  onChange={onChange(setFormData)} onValidate={handleStatusValidation} required/>

                    <Forms.Text name="mobile" regex={Config.validationMap.mobile} value={formData.mobile}
                                onChange={onChange(setFormData)} validationError="onlyCzechPhoneNumbers" required/>

                    <Forms.Text name="email" regex={Config.validationMap.email} validationError="wrongEmailFormat"
                                value={formData.email} required onChange={onChange(setFormData)}/>

                    <Forms.Text password name="password" regex={Config.validationMap.password}
                                validationError="invalidPasswordFormat" value={formData.password} required
                                onChange={onChange(setFormData)}/>

                    <Forms.Text password name="passwordConfirmation" label={t("passwordAgain")}
                                regex={Config.validationMap.password} validationError="invalidPasswordFormat"
                                value={formData.passwordConfirmation} required onChange={onChange(setFormData)}/>

                    <Forms.Date name="dateOfBirth" value={formData.dateOfBirth} required
                                onChange={onChange(setFormData)}/>

                    <div className="agreeWithTermsContainer">
                        <Forms.Checkbox value={agreeWithTerms} name="termsAndConditions"
                                        label={t('agreeWithTerms')}
                                        required
                                        onChange={() => setAgreeWithTerms(!agreeWithTerms)}/>
                        <Icon size={1} alt="" path={iconPath}
                              onClick={() => setIsTermsOpen(!isTermsOpen)}/>
                    </div>
                    {isTermsOpen && termsHtml}

                    <Forms.Checkbox value={agreeWithRules} name="agreeWithRules"
                                    label={t('agreeWithRules')}
                                    required
                                    onChange={() => setAgreeWithRules(!agreeWithRules)}/>
                    <ErrorMessage error={error}/>
                </div>
            </div>
            <div className="modal-footer">
                <Button dark onClick={closeModal} label={t("close")}/>
                <Button type="submit" label={t("signup")} disabled={isLoading}/>
            </div>
        </form>
    );
};

export default SignUpModal;